import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import SchemaOutlinedIcon from "@mui/icons-material/SchemaOutlined";
import { brand, radius } from "../../theme/businessFlowsTheme";
import { useViewConfig } from "../app/ViewConfigContext";
import TypeIcon from "../app/typeIcons";
import { typeInfo } from "../../utils/viewConfig";
import { LOCKED_LABEL } from "../../features";

// A legend is read at a glance: beyond this many types it stops helping.
const MAX_TYPES = 6;

// The explorer's legend: the object types on screen as configured, then the symbols.
export default function ExplorerLegend({ types = [] }) {
  const config = useViewConfig();
  const unique = [...new Set(types.filter(Boolean))];
  const shown = unique.slice(0, MAX_TYPES).map((type) => typeInfo(config, type));
  const moreCount = unique.length - shown.length;

  return (
    <Paper
      elevation={0}
      className="re-legend"
      sx={{
        position: "absolute",
        left: 12,
        right: 12,
        bottom: 12,
        px: 1.5,
        py: 0.75,
        borderRadius: radius.card,
        border: `1px solid ${brand.line}`,
        bgcolor: "rgba(255,255,255,0.96)",
        // A phone has no room for the type keys, but it still needs the one
        // sentence that says what to do with the canvas - it used to get
        // neither, and the explorer opened with no instruction at all.
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        pointerEvents: "none",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5, display: { xs: "none", sm: "flex" } }}>
        {shown.map((info) => (
          <Stack key={info.objectType} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: info.colour, color: "#fff", display: "grid", placeItems: "center" }}>
              <TypeIcon name={info.icon} sx={{ fontSize: 11 }} />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {info.label}
            </Typography>
          </Stack>
        ))}
        {moreCount > 0 && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                px: 0.75,
                height: 16,
                borderRadius: 999,
                bgcolor: "action.selected",
                color: "text.secondary",
                fontSize: 10,
                lineHeight: "16px",
                fontWeight: 700,
              }}
            >
              +{moreCount} more
            </Box>
          </Stack>
        )}
        {/* On a tablet the legend already competes with the canvas for room,
            so these three symbol keys wait for the desktop breakpoint. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
          <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: brand.ink, color: brand.signal, display: "grid", placeItems: "center" }}>
            <SchemaOutlinedIcon sx={{ fontSize: 11 }} />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Has a diagram
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
          <Box sx={{ px: 0.75, height: 16, borderRadius: 999, bgcolor: brand.ink, color: brand.signal, fontSize: 10, lineHeight: "16px", fontWeight: 700 }}>
            12
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Relationship group
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
          <LockRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {LOCKED_LABEL}
          </Typography>
        </Stack>
      </Stack>
      {/* The same instruction, in as many words as the width allows. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "block", sm: "none" } }}>
        Tap an object to see what it connects to.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" }, whiteSpace: "nowrap" }}>
        Click to drill in or out. Drag to move, scroll to zoom.
      </Typography>
    </Paper>
  );
}
